import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Loader2, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line,
} from "recharts";

const PERSPECTIVE_ORDER = ["financiero", "cliente", "proceso", "aprendizaje"];
const PERSPECTIVE_LABEL = { financiero: "Financiero", cliente: "Cliente", proceso: "Proceso", aprendizaje: "Aprendizaje y Crecimiento" };
const PERSPECTIVE_COLOR = { financiero: "#059669", cliente: "#0284c7", proceso: "#d97706", aprendizaje: "#7c3aed" };

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function lastNPeriods(n, from) {
  const [y, m] = from.split("-").map(Number);
  const out = [];
  let year = y, month = m;
  for (let i = 0; i < n; i++) {
    out.unshift(`${year}-${String(month).padStart(2, "0")}`);
    month -= 1;
    if (month === 0) { month = 12; year -= 1; }
  }
  return out;
}

export default function OpsKpiScorecardDashboards() {
  const [period, setPeriod] = useState(currentPeriod());
  const [activePersp, setActivePersp] = useState("financiero");
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const periods = lastNPeriods(6, period);
      const results = await Promise.all(periods.map((p) => api.get(`/ops/kpis/scorecard?period=${p}&scope=consolidado`).then((r) => r.data).catch(() => null)));
      setHistory(results.filter(Boolean));
      setCurrent(results[results.length - 1] || null);
    } catch (e) {
      toast.error("No se pudieron cargar los dashboards del scorecard");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const objetivoVsReal = useMemo(() => {
    if (!current) return [];
    const persp = (current.perspectives || []).find((p) => p.perspective === activePersp);
    return (persp?.kpis || []).map((k) => ({
      name: k.name.length > 18 ? `${k.name.slice(0, 18)}…` : k.name,
      objetivo: k.objetivo ?? null,
      real: k.real ?? null,
    }));
  }, [current, activePersp]);

  const evolution = useMemo(() => {
    return history.map((h) => {
      const row = { period: h.period };
      (h.perspective_summary || []).forEach((s) => { row[s.perspective] = s.rollup_pct; });
      return row;
    });
  }, [history]);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6" data-testid="scorecard-dashboards-page">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-6 h-6 text-[#1A1A1A]" />
        <h1 className="text-2xl font-bold text-slate-900">Dashboards Balanced Scorecard</h1>
      </div>
      <p className="text-sm text-slate-500">Objetivo vs. real por perspectiva y evolución mensual del score.</p>

      <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
        <label className="text-xs font-semibold text-slate-500 uppercase">Período de referencia</label>
        <input
          type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
          className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm" data-testid="dashboards-period-input"
        />
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {/* Evolución del score por perspectiva */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-slate-500" />
              <h2 className="text-sm font-bold text-slate-700">Evolución del % de logro por perspectiva (últimos 6 meses)</h2>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={evolution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                <Tooltip />
                <Legend />
                {PERSPECTIVE_ORDER.map((p) => (
                  <Line key={p} type="monotone" dataKey={p} name={PERSPECTIVE_LABEL[p]} stroke={PERSPECTIVE_COLOR[p]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Objetivo vs Real por perspectiva seleccionada */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-sm font-bold text-slate-700">Objetivo vs. real — {PERSPECTIVE_LABEL[activePersp]} ({period})</h2>
              <div className="flex gap-1.5">
                {PERSPECTIVE_ORDER.map((p) => (
                  <button
                    key={p}
                    onClick={() => setActivePersp(p)}
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${activePersp === p ? "text-white border-transparent" : "text-slate-500 border-slate-200 hover:bg-slate-50"}`}
                    style={activePersp === p ? { backgroundColor: PERSPECTIVE_COLOR[p] } : {}}
                    data-testid={`dashboards-tab-${p}`}
                  >
                    {PERSPECTIVE_LABEL[p]}
                  </button>
                ))}
              </div>
            </div>
            {objetivoVsReal.length === 0 ? (
              <div className="text-sm text-slate-400 py-10 text-center">Sin indicadores en esta perspectiva</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(280, objetivoVsReal.length * 34)}>
                <BarChart data={objetivoVsReal} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="objetivo" name="Objetivo" fill="#cbd5e1" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="real" name="Real" fill={PERSPECTIVE_COLOR[activePersp]} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}
